// Hand-written: the pull quote. Figma 6377:88236.
//
// A `blue-600` band at `Radius/l`, 44px of padding (43.5 by 45 in 6373:16221 - it was 120
// by 96 until 2026-10-30, which made the band taller than the design's 765), holding a
// glass panel with 96px of its own padding at `Radius/m`: 5% white, a 4.5px backdrop blur and a hairline border. Inside, a small
// "Notka" label, the quotation at `h1` (60px), and at the foot a 72px round portrait
// beside the name and role.
//
// **The file's own code export claims that panel border is `#30ff97`** - bright green.
// Sampling the rendered PNG says otherwise: the border pixel is rgb(37,94,226) over an
// rgb(21,76,227) interior, which is white at about 10% and no green anywhere. The
// render is the truth here, so the border is `white/10`. Added to the list of
// contradictions in the file that a designer has to settle.
//
// The quotation marks are the editor's: the design shows them inside the text, and a
// component that adds its own would double them the moment someone types a quote with
// them, or attach them to a fragment that should not have them.
import { SectionShell } from '../primitives';
import { Image } from '../../components/ui/Image';
import type { QuoteSection } from '../../types/sections';

export default function Quote({ data }: { data: QuoteSection }) {
  const portrait = data.media?.image;
  // The design's quotation is five lines at h1 and the band is 765 tall. A longer one steps
  // down to h2 rather than growing the band past that - and never gets cut off.
  const long = (data.quote?.length ?? 0) > 230;

  return (
    <SectionShell settings={data.settings} name="quote">
      <div className="rounded-l bg-blue-600 p-4 sm:p-8 lg:p-[44px]">
        <div className="flex flex-col gap-12 rounded-m border border-white/10 bg-white/5 p-6 backdrop-blur-[4.5px] sm:p-12 lg:p-24">
          <div className="flex flex-col gap-8 text-white">
            {data.eyebrow?.label ? (
              <p className="text-body-sm text-white/90">{data.eyebrow.label}</p>
            ) : null}
            {/* A quotation is a quotation: <blockquote> is what it is, and the text is
                already the visual anchor of the section, so it is not a heading. */}
            <blockquote className={`font-normal ${long ? 'text-h3 lg:text-h2' : 'text-h3 lg:text-h1'}`}>{data.quote}</blockquote>
          </div>

          {data.personName || portrait ? (
            <figcaption className="flex items-center gap-2 text-white">
              {portrait ? (
                <Image
                  media={portrait}
                  alt={data.media?.alt ?? data.personName ?? ''}
                  sizes="72px"
                  className="h-[72px] w-[72px] shrink-0 rounded-full object-cover"
                />
              ) : null}
              <div className="flex flex-col justify-center gap-4">
                {data.personName ? <p className="text-body-md font-medium">{data.personName}</p> : null}
                {data.personRole ? <p className="max-w-[155px] text-caption">{data.personRole}</p> : null}
              </div>
            </figcaption>
          ) : null}
        </div>
      </div>
    </SectionShell>
  );
}
